/**
 * Settle an income-tax payable from a bank-feed outflow with the agency reference
 * (EFTPS EFT acknowledgement / state-portal confirmation). Posts Dr payable / Cr bank.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Landmark, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { API_BASE } from '../../utils/apiUrl';
import { usStateLabel } from '../../lib/payrollOptions';

const fmt = (n, ccy) => new Intl.NumberFormat('en-US', { style: 'currency', currency: ccy || 'USD' }).format(Number(n || 0));

export const TaxPaymentDialog = ({ open, onOpenChange, transaction, currency, onComplete }) => {
  const [opts, setOpts] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ tax_code: '', fiscal_year: '', payment_type: 'estimated', instalment_n: '', state_code: '', agency: '', reference: '', notes: '', fx_rate: '', settled_base_amount: '' });
  const [fx, setFx] = useState(null);

  useEffect(() => {
    if (!open || !transaction?.id) return;
    setFx(null);
    const params = form.fx_rate ? { fx_rate: form.fx_rate } : {};
    axios.get(`${API_BASE}/tax-payments/fx-preview/${transaction.id}`, { params })
      .then(({ data }) => { setFx(data); if (data.is_foreign) setForm((f) => ({ ...f, settled_base_amount: f.settled_base_amount || data.bank_base_amount })); })
      .catch((e) => setFx({ error: e.response?.data?.detail || 'Rate lookup failed' }));
  }, [open, transaction?.id, form.fx_rate]);

  useEffect(() => {
    if (!open) return;
    setOpts(null);
    axios.get(`${API_BASE}/tax-payments/options`).then(({ data }) => {
      setOpts(data);
      const first = data.taxes.find((t) => t.applicable_now) || data.taxes[0];
      const fy = data.latest_snapshot?.fiscal_year || data.fiscal_years[0] || '';
      setForm((f) => ({ ...f, tax_code: first?.tax_code || '', fiscal_year: fy, agency: first?.agency || '',
        state_code: first?.state_scoped ? (data.nexus_states[0] || '') : '' }));
    }).catch((e) => toast.error(e.response?.data?.detail || 'Could not load tax payment options'));
  }, [open]);

  const tax = useMemo(() => opts?.taxes.find((t) => t.tax_code === form.tax_code), [opts, form.tax_code]);
  const instalments = useMemo(() => {
    if (!opts || !tax) return [];
    return tax.state_scoped ? opts.latest_snapshot.state_instalments : (opts.latest_snapshot.federal_instalments || []);
  }, [opts, tax]);

  const pickTax = (code) => {
    const t = opts.taxes.find((x) => x.tax_code === code);
    setForm((f) => ({ ...f, tax_code: code, state_code: t?.state_scoped ? (f.state_code || opts.nexus_states[0] || '') : '',
      agency: t?.state_scoped ? (opts.state_portals[f.state_code || opts.nexus_states[0]] || t.agency) : t?.agency || '' }));
  };
  const pickState = (st) => setForm((f) => ({ ...f, state_code: st, agency: opts.state_portals[st] || `${st} Department of Revenue portal` }));

  const submit = async () => {
    if (!form.reference.trim()) return toast.error('Enter the agency confirmation / EFT acknowledgement number');
    setSaving(true);
    try {
      const { data } = await axios.post(`${API_BASE}/tax-payments/from-bank/${transaction.id}`, {
        ...form, instalment_n: form.instalment_n ? Number(form.instalment_n) : null, state_code: form.state_code || null,
        fx_rate: form.fx_rate ? Number(form.fx_rate) : null, settled_base_amount: form.settled_base_amount ? Number(form.settled_base_amount) : null,
      });
      toast.success(data.message || 'Tax payment recorded');
      onOpenChange(false);
      onComplete?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not record the tax payment');
    } finally {
      setSaving(false);
    }
  };

  if (!transaction) return null;
  const amount = Math.abs(Number(transaction.amount || 0));
  const ccy = transaction.currency || currency;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="tax-payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Landmark className="w-4 h-4 text-[#283F8B]" /> Record income tax payment</DialogTitle>
          <DialogDescription>Settles the tax payable from this bank outflow and keeps the agency reference for the return. Journal: Dr tax payable / Cr bank.</DialogDescription>
        </DialogHeader>
        <div className="p-3 bg-muted/50 rounded-lg text-sm flex justify-between" data-testid="tax-payment-source">
          <span className="truncate">{transaction.description}</span>
          <span className="font-semibold shrink-0" data-testid="tax-payment-amount">{fmt(amount, ccy)}</span>
        </div>
        {!opts ? <div className="py-6 text-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading tax payables…</div> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label className="text-[10px] uppercase">Tax being paid</Label>
              <Select value={form.tax_code} onValueChange={pickTax}>
                <SelectTrigger data-testid="tax-payment-tax"><SelectValue placeholder="Choose the tax" /></SelectTrigger>
                <SelectContent>
                  {opts.taxes.map((t) => (
                    <SelectItem key={t.tax_code} value={t.tax_code} data-testid={`tax-payment-tax-${t.tax_code}`}>
                      {t.label} · GL {t.payable_gl} · balance {fmt(t.payable_balance, ccy)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {tax && (
                <p className="text-[11px] text-muted-foreground mt-1" data-testid="tax-payment-balance-hint">
                  Payable {tax.payable_gl} {tax.payable_gl_name ? `(${tax.payable_gl_name})` : ''} balance {fmt(tax.payable_balance, ccy)}.
                  {amount > tax.payable_balance && ' This payment exceeds the accrued balance - the excess sits as prepaid tax until the accrual catches up.'}
                </p>
              )}
            </div>
            {tax?.state_scoped && (
              <div>
                <Label className="text-[10px] uppercase">State</Label>
                <Select value={form.state_code} onValueChange={pickState}>
                  <SelectTrigger data-testid="tax-payment-state"><SelectValue placeholder="State" /></SelectTrigger>
                  <SelectContent>{opts.nexus_states.map((s) => <SelectItem key={s} value={s}>{usStateLabel(s)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-[10px] uppercase">Fiscal year</Label>
              <Select value={form.fiscal_year} onValueChange={(v) => setForm({ ...form, fiscal_year: v })}>
                <SelectTrigger data-testid="tax-payment-fy"><SelectValue /></SelectTrigger>
                <SelectContent>{opts.fiscal_years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase">Payment type</Label>
              <Select value={form.payment_type} onValueChange={(v) => setForm({ ...form, payment_type: v })}>
                <SelectTrigger data-testid="tax-payment-type"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(opts.payment_types).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.payment_type === 'estimated' && (
              <div>
                <Label className="text-[10px] uppercase">Instalment</Label>
                <Select value={form.instalment_n ? String(form.instalment_n) : 'none'} onValueChange={(v) => setForm({ ...form, instalment_n: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="tax-payment-instalment"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not specified</SelectItem>
                    {(instalments.length ? instalments : [1, 2, 3, 4].map((n) => ({ n }))).map((i) => (
                      <SelectItem key={i.n} value={String(i.n)}>Q{i.n}{i.due_date ? ` · due ${i.due_date}` : ''}{i.amount ? ` · ${fmt(i.amount, ccy)}` : ''}{i.status ? ` · ${i.status.replace('_', ' ')}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-[10px] uppercase">Agency / portal</Label>
              <Input value={form.agency} onChange={(e) => setForm({ ...form, agency: e.target.value })} data-testid="tax-payment-agency" />
            </div>
            <div>
              <Label className="text-[10px] uppercase">{tax && !tax.state_scoped && opts.country === 'US' ? 'EFTPS EFT acknowledgement #' : 'Confirmation / reference #'}</Label>
              <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder={opts.country === 'US' ? 'e.g. 270912345678901' : 'Receipt / confirmation number'} data-testid="tax-payment-reference" />
            </div>
            {fx?.is_foreign && (
              <div className="md:col-span-2 p-3 border rounded-lg bg-amber-50/60 space-y-2" data-testid="tax-payment-fx">
                <p className="text-xs font-medium">Foreign-currency account: {fx.bank_currency} {fx.foreign_amount.toLocaleString()} → {fx.base_currency}</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase">Rate ({fx.bank_currency}→{fx.base_currency})</Label>
                    <Input type="number" step="any" value={form.fx_rate} placeholder={`${fx.rate} (${fx.rate_source}${fx.rate_date ? ` ${fx.rate_date}` : ''})`}
                      onChange={(e) => setForm({ ...form, fx_rate: e.target.value, settled_base_amount: '' })} data-testid="tax-payment-fx-rate" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase">Bank leg in {fx.base_currency}</Label>
                    <Input value={fmt(fx.bank_base_amount, fx.base_currency)} readOnly data-testid="tax-payment-fx-bank-base" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase">Settled on payable ({fx.base_currency})</Label>
                    <Input type="number" step="0.01" value={form.settled_base_amount} onChange={(e) => setForm({ ...form, settled_base_amount: e.target.value })} data-testid="tax-payment-settled-base" />
                  </div>
                </div>
                {(() => { const d = Number(fx.bank_base_amount) - Number(form.settled_base_amount || fx.bank_base_amount); return Math.abs(d) >= 0.01 ? (
                  <p className={`text-[11px] ${d > 0 ? 'text-red-700' : 'text-emerald-700'}`} data-testid="tax-payment-fx-diff">
                    Realised FX {d > 0 ? 'loss' : 'gain'} {fmt(Math.abs(d), fx.base_currency)} will be booked automatically.
                  </p>
                ) : <p className="text-[11px] text-muted-foreground">No FX difference - the payable is settled at the bank leg's {fx.base_currency} value. Enter the {fx.base_currency} amount the agency received to book a realised gain/loss.</p>; })()}
              </div>
            )}
            {fx?.error && <p className="md:col-span-2 text-[11px] text-red-700" data-testid="tax-payment-fx-error">{fx.error}</p>}
            <div className="md:col-span-2">
              <Label className="text-[10px] uppercase">Notes (optional)</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="tax-payment-notes" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="tax-payment-cancel">Cancel</Button>
          <Button className="bg-[#283F8B]" onClick={submit} disabled={saving || !opts || !form.tax_code || (fx?.is_foreign === undefined && !fx?.error) || !!fx?.error} data-testid="tax-payment-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Landmark className="w-4 h-4 mr-1" />} Post payment {fx?.is_foreign ? fmt(form.settled_base_amount || fx.bank_base_amount, fx.base_currency) : fmt(amount, ccy)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TaxPaymentDialog;
